import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { ViewportMetrics } from '../hooks/useViewportMetrics';

export type DeviceType = 'desktop' | 'iphone-14-pro-landscape' | 'ipad-mini-landscape' | 'samsung-s22-landscape';

export interface SimulatedDevice {
  id: DeviceType;
  name: string;
  width: number;
  height: number;
  safeArea: { top: number; right: number; bottom: number; left: number };
  isTouch: boolean;
}

export const DEVICES: Record<DeviceType, SimulatedDevice> = {
  'desktop': {
    id: 'desktop', name: 'Desktop', width: 0, height: 0,
    safeArea: { top: 0, right: 0, bottom: 0, left: 0 }, isTouch: false
  },
  'iphone-14-pro-landscape': {
    id: 'iphone-14-pro-landscape', name: 'iPhone 14 Pro (Landscape)',
    width: 852, height: 393,
    safeArea: { top: 0, right: 59, bottom: 21, left: 59 },
    isTouch: true
  },
  'ipad-mini-landscape': {
    id: 'ipad-mini-landscape', name: 'iPad Mini (Landscape)',
    width: 1133, height: 744,
    safeArea: { top: 0, right: 0, bottom: 20, left: 0 },
    isTouch: true
  },
  'samsung-s22-landscape': {
    id: 'samsung-s22-landscape', name: 'Galaxy S22 (Landscape)',
    width: 915, height: 412,
    safeArea: { top: 0, right: 0, bottom: 0, left: 32 },
    isTouch: true
  }
};

export const SimulatorContext = createContext<{
  activeDevice: DeviceType;
  setActiveDevice: (d: DeviceType) => void;
  mockViewport: ViewportMetrics | null;
}>({
  activeDevice: 'desktop',
  setActiveDevice: () => {},
  mockViewport: null
});

export const SimulatorProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [activeDevice, setActiveDevice] = useState<DeviceType>('desktop');

  const mockViewport: ViewportMetrics | null = activeDevice === 'desktop' ? null : {
    width: DEVICES[activeDevice].width,
    height: DEVICES[activeDevice].height,
    layoutWidth: DEVICES[activeDevice].width,
    layoutHeight: DEVICES[activeDevice].height,
    offsetTop: 0,
    offsetLeft: 0,
    aspectRatio: DEVICES[activeDevice].width / DEVICES[activeDevice].height,
    orientation: 'landscape',
    isTouch: DEVICES[activeDevice].isTouch,
    hasVisualViewport: true,
    safeArea: DEVICES[activeDevice].safeArea,
  };

  return (
    <SimulatorContext.Provider value={{ activeDevice, setActiveDevice, mockViewport }}>
      {children}
    </SimulatorContext.Provider>
  );
};

export const useSimulator = () => useContext(SimulatorContext);
